import { localStorageService } from "#services";
import {
    UiState,
    UiStateActionTypeIdentifiers,
    UiStateActionTypes,
} from "#store/ui/uiTypes";

/******************************************************************************
 *
 *  The state reducer function that handles the applicationState items
 *
 * @param state
 * @param action
 *
 ******************************************************************************/

const HISTORY_MAX_SIZE = 20;

export function uiStateReducer(
    state = localStorageService.read(),
    action: UiStateActionTypes
): UiState {
    switch (action.type) {
        case UiStateActionTypeIdentifiers.SAVE_SESSION: {
            const newState = state.savedSessions.filter(
                x => x.label !== action.payload.label
            );
            if (action.payload) {
                newState.push(action.payload);
            }
            return {
                ...state,
                savedSessions: newState,
            };
        }
        case UiStateActionTypeIdentifiers.LOAD_SESSION: {
            const session = state.savedSessions.find(
                x => x.label !== action.payload
            );
            if (session) {
                return {
                    ...state,
                    activeInterpretations: session.interpretations,
                };
            } else {
                console.warn("Tried to load non-existing session.");
                return state;
            }
        }
        case UiStateActionTypeIdentifiers.DELETE_SESSION: {
            const newState = state.savedSessions.filter(
                x => x.label !== action.payload
            );
            return {
                ...state,
                savedSessions: newState,
            };
        }
        case UiStateActionTypeIdentifiers.SET_DATASOURCE: {
            return {
                ...state,
                selectedDatasource: action.payload,
            };
        }
        case UiStateActionTypeIdentifiers.SET_QUERY_LANGUAGE: {
            return {
                ...state,
                selectedQueryLanguage: action.payload,
            };
        }
        case UiStateActionTypeIdentifiers.SET_THEME: {
            return {
                ...state,
                theme: action.payload,
            };
        }
        case UiStateActionTypeIdentifiers.SET_ROWS_PER_TABLE_LIMIT: {
            return {
                ...state,
                rowsPerTableLimit: action.payload,
            };
        }
        case UiStateActionTypeIdentifiers.SET_INTERPRETATIONS_LIMIT: {
            return {
                ...state,
                maxInterpretationsLimit: action.payload,
            };
        }
        case UiStateActionTypeIdentifiers.TOGGLE_TABLE_FAV_STATE: {
            const index = state.activeInterpretations.indexOf(action.payload);
            if (index < 0) {
                return {
                    ...state,
                    activeInterpretations: state.activeInterpretations.concat(
                        action.payload
                    ),
                };
            } else {
                return {
                    ...state,
                    activeInterpretations: state.activeInterpretations.filter(
                        x => x !== action.payload
                    ),
                };
            }
        }
        case UiStateActionTypeIdentifiers.SET_TABLE_FAV_STATE: {
            const newState = state.activeInterpretations.filter(
                x => x !== action.payload.id
            );
            if (action.payload.value) {
                newState.push(action.payload.id);
            }
            return {
                ...state,
                activeInterpretations: newState,
            };
        }
        case UiStateActionTypeIdentifiers.SET_COLUMN_FAV_STATE: {
            const index = state.favoriteColumns.indexOf(action.payload.column);
            if (!action.payload.value) {
                // action is "remove" from col favs
                if (index < 0) {
                    return state;
                    // does not exist => noop
                } else {
                    return {
                        ...state,
                        favoriteColumns: state.favoriteColumns.filter(
                            x => x !== action.payload.column
                        ),
                    };
                }
            } else {
                // action is "add" to col favs
                if (index >= 0) {
                    // does exist => noop
                    return state;
                } else {
                    return {
                        ...state,
                        favoriteColumns: state.favoriteColumns.concat(
                            action.payload.column
                        ),
                    };
                }
            }
        }
        case UiStateActionTypeIdentifiers.TOGGLE_COLUMN_FAV_STATE: {
            const index = state.favoriteColumns.indexOf(action.payload);
            if (index < 0) {
                return {
                    ...state,
                    favoriteColumns: state.favoriteColumns.concat(
                        action.payload
                    ),
                };
            } else {
                return {
                    ...state,
                    favoriteColumns: state.favoriteColumns.filter(
                        x => x !== action.payload
                    ),
                };
            }
        }
        case UiStateActionTypeIdentifiers.SET_RELEVANCE_WEIGHT: {
            return {
                ...state,
                doiWeights: [
                    Math.min(Math.max(action.payload, -1), 1),
                    state.doiWeights[1],
                    state.doiWeights[2],
                    state.doiWeights[3],
                ],
            };
        }
        case UiStateActionTypeIdentifiers.SET_COMPARISON_WEIGHT: {
            return {
                ...state,
                doiWeights: [
                    state.doiWeights[0],
                    Math.min(Math.max(action.payload, -1), 1),
                    state.doiWeights[2],
                    state.doiWeights[3],
                ],
            };
        }
        case UiStateActionTypeIdentifiers.SET_QUERY_WEIGHT: {
            return {
                ...state,
                doiWeights: [
                    state.doiWeights[0],
                    state.doiWeights[1],
                    Math.min(Math.max(action.payload, -1), 1),
                    state.doiWeights[3],
                ],
            };
        }
        case UiStateActionTypeIdentifiers.SET_FAVORITE_WEIGHT: {
            return {
                ...state,
                doiWeights: [
                    state.doiWeights[0],
                    state.doiWeights[1],
                    state.doiWeights[2],
                    Math.min(Math.max(action.payload, -1), 1),
                ],
            };
        }
        case UiStateActionTypeIdentifiers.SET_QUERY_STRING: {
            return {
                ...state,
                query: action.payload,
            };
        }
        case UiStateActionTypeIdentifiers.ADD_HISTORY_ITEM: {
            return {
                ...state,
                history: [action.payload, ...state.history].slice(
                    0,
                    HISTORY_MAX_SIZE
                ),
            };
        }
        case UiStateActionTypeIdentifiers.ADD_HISTORY_ITEMS: {
            return {
                ...state,
                history: [...action.payload, ...state.history].slice(
                    0,
                    HISTORY_MAX_SIZE
                ),
            };
        }
        default: {
            return state;
        }
    }
}
